import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef } from 'react';
import { catchResponseError } from 'packages/helpers/helpers';
import { sleep } from '@bynder-studio/render-core';

type ContextValue = Map<string, Promise<Response>>;
const AssetsCacheContext = createContext<ContextValue>(new Map());

type ProviderProps = PropsWithChildren<{
    template: any;
}>;

export function AssetsCacheProvider({ template, children }: ProviderProps) {
    const value = useRef<ContextValue>(new Map());

    useEffect(() => {
        const nodes = template.pages.flatMap((page) => page.elements);
        const map = value.current;
        let isMounted = true;

        const timeoutId = setTimeout(
            async () => {
                const entries = prefetchAssets(nodes);

                for await (const [url, prefetch] of entries) {
                    if (!isMounted) {
                        return;
                    }

                    if (map.has(url)) {
                        continue;
                    }

                    map.set(url, prefetch());
                }
            },
            // start prefetching with a delay
            1000 * 60 * 1,
        );

        return () => {
            isMounted = false;
            map.clear();
            clearTimeout(timeoutId);
        };
    }, [template]);

    return <AssetsCacheContext.Provider value={value.current}>{children}</AssetsCacheContext.Provider>;
}

export function useAssetCache(url: string | undefined): Promise<Response> {
    const assetContext = useContext(AssetsCacheContext);

    const asset = useMemo(() => {
        if (!url) {
            return;
        }

        const cached = assetContext.get(url);

        if (cached) {
            return cached.then((res) => res.clone());
        }

        const res = fetch(url);

        assetContext.set(
            url,
            res.then((r) => r.clone()),
        );

        return res;
    }, [url, assetContext]);

    return asset;
}

function* prefetchAssets(nodes: any[]) {
    for (const node of nodes) {
        if (node.type === 'GROUP') {
            yield* prefetchAssets(node.children);
        }

        if (['IMAGE', 'VIDEO', 'GLOBAL_AUDIO'].includes(node.type) && node?.properties?.value) {
            const { value: v } = node.properties;

            const urls = [v.thumbnail, v.previewUrl, v.url].filter(Boolean);

            for (const url of urls) {
                const prefetch = () => fetch(url);
                // eslint-disable-next-line no-await-in-loop
                yield [
                    url,
                    () =>
                        prefetch()
                            .catch(() => sleep(200).then(prefetch))
                            .catch(() => sleep(1000).then(prefetch))
                            .catch(() => sleep(5000).then(prefetch))
                            .catch(catchResponseError),
                ];
            }
        }
    }
}
